import { PayloadAction } from '@reduxjs/toolkit';

import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import { errorActions } from 'store/error/slice';
import { insightActions } from 'store/insight/slice';
import { sliceFactory, thunkActionFactory } from 'store/utils';
import { Sale, SaleAcceptStatus, SubscriptionInfo } from 'types/Sale';

const setError = errorActions.set;
const splunkReporter = SplunkReporter.getInstance();
const logger = 'store/sale/slice';

// @ts-expect-error
// type mismatch
const initialState = {
  amount: null,
  contacts: null,
  currencyInfo: null,
  entity: null,
  freight: null,
  lines: null,
  paymentEnabledForLocale: null,
  paymentDetailMessage: null,
  receivable: null,
  referenceNumber: null,
  shipping: null, //not sure if can be calculated
  shippingAmount: null, // does not exist on original scs invoices
  tax: null,
  txnDate: null,
  type: null,
  isPartiallyPaid: false,
  riskProfileToken: null,
  isEditAmountDisabled: false,
  batchProcessing: null, // Stores batchId and totalInvoices for batch processing screen
  batchPaymentComplete: null, // Stores batch payment completion data for complete screen
  batchFullFailureError: false, // True when entire batch failed — keeps user on payment page with error banner
} as Sale;

const { reducer, actions } = sliceFactory({
  name: 'sale',
  initialState,
  reducers: {
    updateSaleData(
      state,
      action: PayloadAction<{
        balance: Sale['receivable']['balance'];
        isPartiallyPaid: Sale['isPartiallyPaid'];
      }>
    ) {
      const { balance, isPartiallyPaid } = action.payload;
      state.receivable.balance = balance;
      state.isPartiallyPaid = isPartiallyPaid;
    },
    updateEstimateStatus(state, action: PayloadAction<{ status: SaleAcceptStatus }>) {
      if (state.acceptStatusInfo) {
        state.acceptStatusInfo.acceptStatus = action.payload.status;
      }
    },
    updateNewBalance(state, action: PayloadAction<{ newBalance: number }>) {
      const { newBalance } = action.payload;
      state.receivable.balance = newBalance;
    },
    updateSubscriptionInfo(state, action: PayloadAction<SubscriptionInfo['subscriptionInfo']>) {
      if (state.subscriptionInfo) {
        state.subscriptionInfo.subscriptionInfo = action.payload;
      }
    },
    setIsEditAmountDisabled(state, action: PayloadAction<boolean>) {
      state.isEditAmountDisabled = action.payload;
    },
    setBatchProcessing(
      state,
      action: PayloadAction<{
        batchId: string;
        totalInvoices: number;
        selectedInvoices: any[];
        allBatchInvoices: any;
        merchantWalletSaveData?: any;
        creditCardInfo?: any;
        achInfo?: any;
      } | null>
    ) {
      state.batchProcessing = action.payload;
    },
    setBatchPaymentComplete(
      state,
      action: PayloadAction<{
        batchId: string;
        batchInvoices: any[];
        merchant: string;
        paymentMethod: string;
        totalCharged: number;
        currency: string;
        invoiceMap: Record<
          string,
          { docNumber?: string; saleId?: string; link: string; dueDate?: string; token: string }
        >;
      } | null>
    ) {
      state.batchPaymentComplete = action.payload;
    },
    setIsFetchingBatchPDFs(state, action: PayloadAction<boolean>) {
      state.isFetchingBatchPDFs = action.payload;
    },
    setBatchFullFailureError(state, action: PayloadAction<boolean>) {
      state.batchFullFailureError = action.payload;
    },
  },
});

export const saleReducer = reducer;
export const saleActions = actions;

export const setStatusAccept = thunkActionFactory(async ({ dispatch, businessLogic }) => {
  try {
    await businessLogic.sale.setStatusToAccepted();
    dispatch(
      saleActions.updateEstimateStatus({ status: businessLogic.sale.status as SaleAcceptStatus })
    );
  } catch (e) {
    dispatch(
      setError({ title: 'SET_SALE_STATUS_ERROR_TITLE', body: 'SET_SALE_STATUS_ERROR_BODY' })
    );
  }
});

export const setStatusDeclined = thunkActionFactory(async ({ dispatch, businessLogic }) => {
  try {
    await businessLogic.sale.setStatusToDeclined();
    dispatch(
      saleActions.updateEstimateStatus({ status: businessLogic.sale.status as SaleAcceptStatus })
    );
  } catch (e) {
    dispatch(
      setError({ title: 'SET_SALE_STATUS_ERROR_TITLE', body: 'SET_SALE_STATUS_ERROR_BODY' })
    );
  }
});

export const updateSaleData = thunkActionFactory(async ({ dispatch, businessLogic }) => {
  dispatch(insightActions.updateInProgress());

  try {
    dispatch(saleActions.updateSaleData(await businessLogic.sale.updateSaleData()));
    dispatch(insightActions.updateSuccess());
  } catch (e: any) {
    dispatch(insightActions.updateFail());
  }
});

export const updateNewBalance = thunkActionFactory<{
  amountPaid: number;
}>(({ payload, dispatch, businessLogic }) => {
  const { amountPaid } = payload || {};
  businessLogic.sale.balanceAfterPayment = { amountPaid };
  dispatch(
    saleActions.updateNewBalance({
      newBalance: businessLogic.sale.balance,
    })
  );
});

export const payBatch = thunkActionFactory<any>(async ({ payload, businessLogic, state }) => {
  try {
    const response = await businessLogic.sale.payBatch(payload);
    return response;
  } catch (error: any) {
    const errorDetails: any = {
      message: error?.message || 'Batch payment failed',
      stack: error?.stack,
    };
    if (error?.response?.data) {
      errorDetails.responseData = error.response.data;
    }

    splunkReporter.contextual({
      sessionId: state.config?.ssrtid,
      logInfo: { logLevel: 'error', logger },
      event: 'pay' as any,
      action: 'batchPayment' as any,
      error: errorDetails,
    });
    throw error;
  }
});

export const updateSubscriptionInfo = thunkActionFactory<SubscriptionInfo['subscriptionInfo']>(
  ({ dispatch, payload }) => {
    if (payload) {
      dispatch(saleActions.updateSubscriptionInfo(payload));
    }
  }
);

export const fetchBatchPaymentStatus = thunkActionFactory<{ batchId: string }>(
  async ({ payload, businessLogic }) => {
    const { batchId } = payload || {};
    if (!batchId) {
      throw new Error('batchId is required');
    }
    const response = await businessLogic.sale.fetchBatchPaymentStatus(batchId);
    return response;
  }
);

/**
 * Save payment method to merchant wallet after batch processing completes
 * Called only when at least one invoice payment succeeded
 */
export const saveMerchantWallet = thunkActionFactory<{
  merchantWalletSaveData: any;
  creditCardInfo?: any;
  achInfo?: any;
}>(async ({ payload, businessLogic, state }) => {
  try {
    const response = await businessLogic.sale.saveMerchantWallet(payload);
    return response;
  } catch (error: any) {
    // Extract error details without PII
    const errorDetails: any = {
      message: error?.message || 'Failed to save to merchant wallet',
      stack: error?.stack,
      status: error?.status || error?.response?.status,
    };

    // Include error code if available (no PII)
    if (error?.response?.data?.errorCode) {
      errorDetails.errorCode = error.response.data.errorCode;
    }

    // Include error type/category if available (no PII)
    if (error?.response?.data?.errorType) {
      errorDetails.errorType = error.response.data.errorType;
    }

    splunkReporter.contextual({
      sessionId: state.config?.ssrtid,
      logInfo: { logLevel: 'error', logger },
      event: 'batchPayment',
      action: 'saveMerchantWallet',
      error: errorDetails,
    });
    throw error;
  }
});
